import * as React from "react"
import { Dialog as DialogPrimitive } from "radix-ui"
import { X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { useVisualViewportBox } from "@/lib/useVisualViewport"

/** What `useDirtyGuard` and the discard prompt share with the dialog around them. */
interface DirtyGuard {
  setDirty: (dirty: boolean) => void
  asking: boolean
  keep: () => void
  discard: () => void
}

const DirtyGuardContext = React.createContext<DirtyGuard | null>(null)

/**
 * The dialog root (Radix), plus the draft guard of spec-64-u2 (U-076, IA-15): while a
 * form inside has unsaved input (`useDirtyGuard(true)`), Escape, a click beside the
 * dialog and the close button no longer throw it away but ask "Concept verwerpen?".
 * A deliberate close by the caller (its own "Annuleren", or setting `open` to false)
 * goes through unasked. Without a guard it behaves exactly like Radix's root.
 */
function Dialog({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const [innerOpen, setInnerOpen] = React.useState(defaultOpen ?? false)
  const open = openProp ?? innerOpen
  const dirty = React.useRef(false)
  const [asking, setAsking] = React.useState(false)

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (openProp === undefined) setInnerOpen(next)
      onOpenChange?.(next)
    },
    [openProp, onOpenChange],
  )

  const guard = React.useMemo<DirtyGuard>(
    () => ({
      setDirty: (next: boolean) => {
        dirty.current = next
      },
      asking,
      keep: () => setAsking(false),
      discard: () => {
        dirty.current = false
        setAsking(false)
        setOpen(false)
      },
    }),
    [asking, setOpen],
  )

  // A dialog the caller closed starts unasked the next time it opens.
  if (!open && asking) setAsking(false)

  return (
    <DirtyGuardContext.Provider value={guard}>
      <DialogPrimitive.Root
        open={open}
        onOpenChange={(next) => {
          if (!next && dirty.current) {
            setAsking(true)
            return
          }
          setOpen(next)
        }}
        {...props}
      />
    </DirtyGuardContext.Provider>
  )
}

/**
 * Marks the form it is called from as holding unsaved input. Inside a `Dialog` it arms
 * the "Concept verwerpen?" prompt; outside one it asks the browser to warn before the
 * page is left (a long note on a page). Pass a cheap boolean, e.g. `text.trim() !== ''`.
 */
function useDirtyGuard(isDirty: boolean): void {
  const setDirty = React.useContext(DirtyGuardContext)?.setDirty
  React.useEffect(() => {
    if (setDirty) {
      setDirty(isDirty)
      return () => setDirty(false)
    }
    if (!isDirty) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
    }
    window.addEventListener("beforeunload", warn)
    return () => window.removeEventListener("beforeunload", warn)
  }, [setDirty, isDirty])
}

/** The question that stops a draft from being thrown away (inside the dialog's focus trap). */
function DiscardPrompt({ guard }: { guard: DirtyGuard }) {
  const titleId = React.useId()
  const descriptionId = React.useId()
  return (
    <div
      role="alertdialog"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      data-slot="dialog-discard-prompt"
      className="sticky bottom-0 z-10 mt-4 flex flex-col gap-3 rounded-lg border border-border bg-card p-4 shadow-[var(--shadow-raised)]"
    >
      <div className="flex flex-col gap-1">
        <p id={titleId} className="font-heading text-base font-semibold text-foreground">
          Concept verwerpen?
        </p>
        <p id={descriptionId} className="text-sm text-muted-foreground">
          Wat je hebt ingevuld, is nog niet bewaard en gaat verloren.
        </p>
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" autoFocus onClick={guard.keep}>
          Verder bewerken
        </Button>
        <Button type="button" variant="destructive" onClick={guard.discard}>
          Verwerpen
        </Button>
      </div>
    </div>
  )
}

const DialogTrigger = DialogPrimitive.Trigger
const DialogClose = DialogPrimitive.Close

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "vonk-dialog-overlay data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  )
}

/**
 * The bottom sheet below md (index.css) restated as utilities. A utility beats a
 * .vonk-* rule, and `max-md:` beats a caller's plain desktop cap (`max-w-md`,
 * `max-h-[85vh]`), which would otherwise pin the sheet to one side of a phone or
 * push its top off-screen once the keyboard shrinks the positioner. Keep these in
 * step with the sheet rule. Callers cap from `md:` up, never `sm:`, which Tailwind
 * sorts after `max-md:` (guarded in test/guards/layers.test.ts).
 */
const SHEET_SIZE = "max-md:w-full max-md:max-w-full max-md:max-h-[min(90svh,calc(100%-24px))]"

/** Width presets (spec-64, U-075). md is the old fixed 32rem; below 768px every
 *  dialog is a bottom sheet whatever its size. */
type DialogSize = "sm" | "md" | "lg" | "xl" | "full"

function DialogContent({
  className,
  children,
  showClose = true,
  size = "md",
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showClose?: boolean
  /** sm 28rem · md 32rem · lg 48rem · xl 64rem · full min(96vw, 1400px). */
  size?: DialogSize
}) {
  // Keyboard rule: the sheet must shrink to the visible area so bottom-anchored
  // inputs stay typable on iOS (same fix as the Sparky sheet, PR #493).
  const box = useVisualViewportBox(true)
  const guard = React.useContext(DirtyGuardContext)
  const { onEscapeKeyDown } = props
  return (
    <DialogPrimitive.Portal>
      <DialogOverlay />
      <div
        className="vonk-dialog-positioner"
        style={box ? { top: box.offsetTop, height: box.height, bottom: "auto" } : undefined}
      >
        <DialogPrimitive.Content
          data-slot="dialog-content"
          data-size={size}
          className={cn(
            "vonk-dialog-content data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 max-md:data-[state=open]:slide-in-from-bottom-6 max-md:data-[state=closed]:slide-out-to-bottom-6",
            SHEET_SIZE,
            className,
          )}
          {...props}
          onEscapeKeyDown={(event) => {
            // Escape on the discard prompt means "keep editing", not "close".
            if (guard?.asking) {
              event.preventDefault()
              guard.keep()
              return
            }
            onEscapeKeyDown?.(event)
          }}
        >
          {children}
          {guard?.asking ? <DiscardPrompt guard={guard} /> : null}
          {showClose ? (
            <DialogPrimitive.Close
              aria-label="Sluiten"
              className="absolute top-4 right-4 rounded-md text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="size-4" />
            </DialogPrimitive.Close>
          ) : null}
        </DialogPrimitive.Content>
      </div>
    </DialogPrimitive.Portal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-1.5", className)}
      {...props}
    />
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("font-heading text-lg font-semibold text-foreground", className)}
      {...props}
    />
  )
}

/** The button row of a dialog: one spacing and order everywhere (secondary first, primary last). */
function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn("mt-4 flex flex-wrap items-center justify-end gap-2", className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogTrigger,
  DialogClose,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  useDirtyGuard,
}
